import { OfferTriggerRules } from "../data/offerTriggers";
import { SECONDS_PER_DAY, utcDay } from "./orders";

/**
 * The `offerTriggers` save section and its rules (MONETISATION_PLAN MP-05): what the save says that may suggest an
 * offer already on sale — a level lost again and again, a return after days away, a new chapter of Mythic-tier raiders —
 * and which suggestions the player has already looked at. A suggestion is a badge (a red dot), never a pop-up; each
 * rule badges at most once a UTC day and its badge clears when the player views what it points at. The app
 * (packages/app/src/offerTriggers.ts) adds the rest of the save (gold, the next upgrade, what is on sale) and the
 * never list's quiet window.
 */

/** The rules, by id: the key their "seen" day is kept under. */
export const TRIGGER_RULES = ["loss", "gold", "mythic", "welcome"] as const;
export type TriggerRule = (typeof TRIGGER_RULES)[number];

const SECONDS_PER_HOUR = 3600;

export interface OfferTriggersJson {
  /** Per level: losses in a row since its last win, and the UTC day of the last of them. */
  losses: Record<string, { n: number; day: number }>;
  /** Per rule: the UTC day its badge was last viewed. */
  seen: Partial<Record<TriggerRule, number>>;
  /** The last chapter whose Master's Collection suggestion was viewed (−1: none). */
  mythicChapter: number;
  /** The Welcome Back window: when it opened (−1: never) and when its pack was bought (−1: not since). */
  welcome: { openedAt: number; boughtAt: number };
}

const isInt = (n: unknown): n is number => Number.isInteger(n);

export class OfferTriggersState {
  losses: Record<string, { n: number; day: number }> = {};
  seen: Partial<Record<TriggerRule, number>> = {};
  mythicChapter = -1;
  welcomeOpenedAt = -1;
  welcomeBoughtAt = -1;

  // ------------------------------------------------------------------ seen

  /** The UTC day `rule`'s badge was last viewed (−1: never). */
  seenDay(rule: TriggerRule): number { return this.seen[rule] ?? -1; }
  /** Whether `rule`'s badge was viewed today (it does not come back until tomorrow at the earliest). */
  seenToday(rule: TriggerRule, now: number): boolean { return this.seenDay(rule) >= utcDay(now); }
  markSeen(rule: TriggerRule, now: number): void { this.seen[rule] = Math.max(this.seenDay(rule), utcDay(now)); }

  // ------------------------------------------------------------------ losses

  /** A battle on `level` ended: a win clears the level's run of losses, a loss adds to it. */
  noteResult(level: number, won: boolean, now: number): void {
    const k = String(level);
    if (won) { delete this.losses[k]; return; }
    this.losses[k] = { n: (this.losses[k]?.n ?? 0) + 1, day: utcDay(now) };
  }

  /**
   * The level whose run of losses suggests gear now (0: none): at least `lossStreak` losses in a row, the last of them
   * within `lossDays` UTC days and on a later day than the suggestion was last viewed (a new loss on a new day may
   * badge again; viewing it clears it until then). The most recent such run wins.
   */
  lossLevel(rules: OfferTriggerRules, now: number): number {
    if (rules.lossStreak <= 0) return 0;
    const today = utcDay(now), seen = this.seenDay("loss");
    let best = 0, bestDay = -1;
    for (const [k, r] of Object.entries(this.losses)) {
      if (r.n < rules.lossStreak || r.day <= seen || today - r.day >= rules.lossDays || r.day > today) continue;
      const level = Number(k);
      if (r.day > bestDay || (r.day === bestDay && level > best)) { best = level; bestDay = r.day; }
    }
    return best;
  }

  // ------------------------------------------------------------------ Mythic chapters

  /** Whether reaching `chapter` (0-based) suggests the Master's Collection: a chapter after the last one viewed. */
  mythicDue(chapter: number): boolean { return chapter > this.mythicChapter; }
  markMythicSeen(chapter: number): void { this.mythicChapter = Math.max(this.mythicChapter, chapter); }

  // ------------------------------------------------------------------ Welcome Back

  /**
   * A return after days away (the check-in's welcome-back gift is due): opens the Welcome Back window, at most once
   * every `welcomeEveryDays` (a clock set back opens nothing). Returns whether it opened now.
   */
  openWelcome(rules: OfferTriggerRules, now: number): boolean {
    if (rules.welcomeHours <= 0) return false;
    if (this.welcomeOpenedAt >= 0 && now - this.welcomeOpenedAt < rules.welcomeEveryDays * SECONDS_PER_DAY) return false;
    this.welcomeOpenedAt = now;
    return true;
  }

  /** When the current Welcome Back window ends (null: none ever opened). */
  welcomeEndsAt(rules: OfferTriggerRules): number | null {
    return this.welcomeOpenedAt < 0 ? null : this.welcomeOpenedAt + rules.welcomeHours * SECONDS_PER_HOUR;
  }

  /** The Welcome Back pack is on sale: inside its window and not yet bought in it. */
  welcomeOnSale(rules: OfferTriggerRules, now: number): boolean {
    const ends = this.welcomeEndsAt(rules);
    return ends !== null && now >= this.welcomeOpenedAt && now < ends && this.welcomeBoughtAt < this.welcomeOpenedAt;
  }

  /** The pack was bought (granted once per transaction by Deals): its window is spent. */
  noteWelcomeBought(at: number): void { this.welcomeBoughtAt = Math.max(this.welcomeBoughtAt, at, this.welcomeOpenedAt); }

  /** The window's badge shows until viewed once (viewed on or after the day it opened). */
  welcomeSeen(): boolean { return this.welcomeOpenedAt >= 0 && this.seenDay("welcome") >= utcDay(this.welcomeOpenedAt); }

  // ------------------------------------------------------------------ save

  toJson(): OfferTriggersJson {
    return { losses: { ...this.losses }, seen: { ...this.seen }, mythicChapter: this.mythicChapter, welcome: { openedAt: this.welcomeOpenedAt, boughtAt: this.welcomeBoughtAt } };
  }

  /** Reads a section (anything unreadable is dropped field by field; absent = fresh). */
  static fromJson(j: unknown): OfferTriggersState {
    const s = new OfferTriggersState();
    if (typeof j !== "object" || j === null) return s;
    const src = j as Partial<OfferTriggersJson>;
    if (typeof src.losses === "object" && src.losses !== null) {
      for (const [k, r] of Object.entries(src.losses)) {
        if (/^\d+$/.test(k) && r && isInt(r.n) && r.n > 0 && isInt(r.day)) s.losses[k] = { n: r.n, day: r.day };
      }
    }
    if (typeof src.seen === "object" && src.seen !== null) {
      for (const rule of TRIGGER_RULES) { const d = (src.seen as Record<string, unknown>)[rule]; if (isInt(d)) s.seen[rule] = d; }
    }
    if (isInt(src.mythicChapter)) s.mythicChapter = src.mythicChapter;
    if (src.welcome && isInt(src.welcome.openedAt)) s.welcomeOpenedAt = src.welcome.openedAt;
    if (src.welcome && isInt(src.welcome.boughtAt)) s.welcomeBoughtAt = src.welcome.boughtAt;
    return s;
  }
}
